import { getActiveSessionManager as SessionManager, createSessionWithSignature, genAuthNonce, genSiweMessage, getConfig, normalizeEvmAddress, } from '@bounded-sh/core';
import { setCurrentUser, setAuthLoading } from '../../global';
import { setStoredAuthMethod } from '../stored-auth-method';
import { defaultInjectedEvmProvider, } from './injected-evm-wallet-types';
// Re-export the type surface + discovery helper for callers who import the
// provider module directly.
export { defaultInjectedEvmProvider };
// Must equal the authMethod the registry (auth/index.ts) sets as currentAuthMethod,
// so clearIncompatibleSession() keeps (not wipes) this session on the next init().
const INJECTED_EVM_AUTH_METHOD = 'evm-wallet';
// The /session issuer's discriminator for the SIWE (EIP-4361 secp256k1) exchange.
const SIWE_SESSION_METHOD = 'siwe';
const NO_WALLET_ERR = 'No injected EVM wallet found. Install a wallet like MetaMask (metamask.io) or Rabby and try again, ' +
    'or pass config.evmWalletConfig.getProvider to point at your wallet.';
const NO_SOL_TX_ERR = 'EVM wallet login is a LOGIN-ONLY surface (SIWE + personal_sign) and has no Solana ' +
    'transaction surface: signTransaction/signAndSubmitTransaction are unavailable.';
function normalizeConfig(config) {
    if (config.chainId !== undefined && (!Number.isSafeInteger(config.chainId) || config.chainId <= 0)) {
        throw new Error('EVM wallet chainId must be a positive safe integer');
    }
    return Object.assign({}, config);
}
export class InjectedEvmWalletProvider {
    constructor(config = {}) {
        this.authenticatedAddress = null;
        if (typeof window === 'undefined') {
            throw new Error('InjectedEvmWalletProvider can only be instantiated in a browser environment');
        }
        const normalized = normalizeConfig(config);
        // Singleton so the SDK reuses one instance (mirrors the other providers).
        if (InjectedEvmWalletProvider.instance) {
            InjectedEvmWalletProvider.instance.config = normalized;
            InjectedEvmWalletProvider.instance.authenticatedAddress = null;
            return InjectedEvmWalletProvider.instance;
        }
        this.config = normalized;
        InjectedEvmWalletProvider.instance = this;
    }
    // @user.address (the Solana wallet) is null for an EVM-wallet login: the user
    // authenticated with an EVM wallet and has no Solana address. User.address is
    // typed `string | null`, so this is a plain null, not a cast.
    evmUser(address) {
        return { id: address, address: null, evmAddress: address, provider: this };
    }
    resolveProvider() {
        var _a;
        const provider = (_a = (this.config.getProvider ? this.config.getProvider() : null)) !== null && _a !== void 0 ? _a : defaultInjectedEvmProvider();
        if (!provider)
            throw new Error(NO_WALLET_ERR);
        return provider;
    }
    /** Connect the injected wallet and return its canonical lowercase EVM address. */
    async connectAddress(provider) {
        const accounts = await provider.request({ method: 'eth_requestAccounts' });
        const raw = Array.isArray(accounts) ? accounts[0] : undefined;
        const address = normalizeEvmAddress(raw);
        if (!address)
            throw new Error('Wallet did not return a valid EVM address on connect');
        return address;
    }
    /** Current chain id the wallet is connected to (EIP-155 number), or null. */
    async currentChainId(provider) {
        try {
            const hex = await provider.request({ method: 'eth_chainId' });
            if (typeof hex !== 'string' || !/^0x[0-9a-fA-F]+$/.test(hex))
                return null;
            const n = Number(BigInt(hex));
            return Number.isSafeInteger(n) && n > 0 ? n : null;
        }
        catch (_a) {
            return null;
        }
    }
    async login() {
        var _a;
        setAuthLoading(true);
        try {
            const provider = this.resolveProvider();
            const address = await this.connectAddress(provider);
            // Reuse a still-valid session for THIS wallet.
            const existing = await SessionManager().getSession();
            if (existing && existing.address === address) {
                const user = this.evmUser(address);
                this.authenticatedAddress = address;
                setCurrentUser(user);
                this.markAuthMethod();
                return user;
            }
            // SIWE: nonce -> canonical EIP-4361 prompt -> the wallet personal_sign's it.
            const nonce = await genAuthNonce();
            const appId = (await getConfig()).appId;
            const chainId = (_a = this.config.chainId) !== null && _a !== void 0 ? _a : await this.currentChainId(provider);
            if (chainId === null)
                throw new Error('Could not verify the EVM wallet chain for SIWE login');
            const message = genSiweMessage(address, nonce, appId, this.resolveDomain(), this.resolveUri(), { chainId });
            // personal_sign(message, address) -> 0x r‖s‖v hex (the shape the
            // bounded-auth verifier's recoverSiweSigner consumes). The raw UTF-8
            // message is signed under the EIP-191 envelope the verifier hashes.
            const signature = await provider.request({
                method: 'personal_sign',
                params: [message, address],
            });
            const result = await createSessionWithSignature(address, message, signature, SIWE_SESSION_METHOD);
            await SessionManager().storeSession(address, result.accessToken, result.idToken, result.refreshToken);
            this.markAuthMethod();
            const user = this.evmUser(address);
            this.authenticatedAddress = address;
            setCurrentUser(user);
            return user;
        }
        finally {
            setAuthLoading(false);
        }
    }
    resolveDomain() {
        const actual = window.location.host.toLowerCase();
        if (this.config.domain && this.config.domain.toLowerCase() !== actual) {
            throw new Error('SIWE domain override must match the current browser origin');
        }
        return actual;
    }
    resolveUri() {
        const actual = window.location.origin.toLowerCase();
        if (this.config.uri) {
            let configured;
            try {
                configured = new URL(this.config.uri).origin.toLowerCase();
            }
            catch (_a) {
                throw new Error('SIWE URI override must be an absolute URL');
            }
            if (configured !== actual)
                throw new Error('SIWE URI override must match the current browser origin');
        }
        return actual;
    }
    markAuthMethod() {
        setStoredAuthMethod(INJECTED_EVM_AUTH_METHOD);
    }
    async restoreSession() {
        const session = await SessionManager().getSession();
        if (!session)
            return null;
        // An EVM-wallet session stores the EVM address as its address; @user.address
        // (the Solana wallet) is null for this login.
        const address = normalizeEvmAddress(session.address);
        if (!address) {
            await SessionManager().clearSession();
            return null;
        }
        this.authenticatedAddress = address;
        const user = this.evmUser(address);
        setCurrentUser(user);
        return user;
    }
    async logout() {
        // EIP-1193 has no standard disconnect; clearing the Bounded session is the
        // logout. (wallet_revokePermissions is non-standard/opt-in — skip it.)
        await SessionManager().clearSession();
        this.authenticatedAddress = null;
        setCurrentUser(null);
    }
    // --- Signing surface -------------------------------------------------------
    /** App-facing message signing: EIP-191 personal_sign, returns 0x r‖s‖v hex. */
    async signMessage(message) {
        const provider = this.resolveProvider();
        const address = await this.connectAddress(provider);
        if (!this.authenticatedAddress || address !== this.authenticatedAddress) {
            throw new Error('The connected EVM wallet account changed after login. Sign in again before signing.');
        }
        return provider.request({ method: 'personal_sign', params: [message, address] });
    }
    // Solana-typed methods are structurally unsupported on an EVM wallet, and
    // this provider is login-only, so there is no transaction surface at all.
    async signTransaction(_transaction) {
        throw new Error(NO_SOL_TX_ERR);
    }
    async signAndSubmitTransaction(_transaction, _feePayer) {
        throw new Error(NO_SOL_TX_ERR);
    }
    async getNativeMethods() {
        return this.resolveProvider();
    }
}
InjectedEvmWalletProvider.instance = null;
